import {
  CounterBadge,
  Tab,
  TabList,
  tokens,
  type SelectTabData,
} from "@fluentui/react-components";
import { useMemo } from "react";

import { RefreshControl } from "@/features/documents/RefreshControl";
import { useListCounts } from "@/features/documents/useListCounts";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { useFilterStore } from "@/store/filterStore";
import type {
  FilterState,
  ListMode,
  ListTab,
  ListTabCounts,
} from "@/types/domain";

const LIST_TABS: { key: ListTab; label: string; countKey: keyof ListTabCounts }[] = [
  { key: "needsCategorization", label: "Needs categorization", countKey: "needsCategorization" },
  { key: "needsReview", label: "Needs review", countKey: "needsReview" },
  { key: "approved", label: "Approved", countKey: "approved" },
  { key: "archived", label: "Discarded", countKey: "archived" },
  { key: "failed", label: "Failed", countKey: "failed" },
  { key: "all", label: "All", countKey: "all" },
];

const TEXT_DEBOUNCE_MS = 300;

/**
 * Combined mode toggle + tab strip. One horizontal bar with a vertical
 * divider:
 *
 *   [Emails][Documents]  │  Needs categorization (N) · Needs review (N) · …
 *
 * Tabs render in both modes with mode-specific semantics — the
 * backend resolves to either email-aggregate filters or per-doc
 * filters based on which list endpoint the mode targets. Flex-wraps
 * below ~720px.
 */
export function TopBar() {
  const mode = useFilterStore((s) => s.mode);
  const listTab = useFilterStore((s) => s.listTab);
  const setField = useFilterStore((s) => s.set);

  const subject = useFilterStore((s) => s.subject);
  const from = useFilterStore((s) => s.from);
  const documentType = useFilterStore((s) => s.documentType);
  const dateFrom = useFilterStore((s) => s.dateFrom);
  const dateTo = useFilterStore((s) => s.dateTo);
  const sort = useFilterStore((s) => s.sort);
  const tradeKey = useFilterStore((s) => s.tradeKey);
  const correctedOnly = useFilterStore((s) => s.correctedOnly);

  const debSubject = useDebouncedValue(subject, TEXT_DEBOUNCE_MS);
  const debFrom = useDebouncedValue(from, TEXT_DEBOUNCE_MS);
  const debTradeKey = useDebouncedValue(tradeKey, TEXT_DEBOUNCE_MS);

  const filters = useMemo<FilterState>(
    () => ({
      subject: debSubject,
      from: debFrom,
      documentType,
      listTab,
      dateFrom,
      dateTo,
      sort,
      tradeKey: debTradeKey,
      correctedOnly,
      mode,
    }),
    [
      debSubject,
      debFrom,
      documentType,
      listTab,
      dateFrom,
      dateTo,
      sort,
      debTradeKey,
      correctedOnly,
      mode,
    ],
  );

  const { data: counts } = useListCounts(filters);

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 12,
        padding: "6px 16px 0 16px",
        borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
        background: tokens.colorNeutralBackground1,
      }}
    >
      <TabList
        selectedValue={mode}
        onTabSelect={(_, data: SelectTabData) => {
          setField("mode", data.value as ListMode);
        }}
        size="medium"
      >
        <Tab value="emails">Emails</Tab>
        <Tab value="documents">Documents</Tab>
      </TabList>
      <div
        aria-hidden="true"
        style={{
          width: 1,
          alignSelf: "stretch",
          background: tokens.colorNeutralStroke2,
          margin: "8px 0",
        }}
      />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <TabList
          selectedValue={listTab}
          onTabSelect={(_, data: SelectTabData) => {
            setField("listTab", data.value as ListTab);
          }}
          size="small"
        >
          {LIST_TABS.map((t) => {
            const count = counts?.[t.countKey];
            return (
              <Tab key={t.key} value={t.key}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {t.label}
                  {typeof count === "number" ? (
                    <CounterBadge
                      appearance="ghost"
                      color="informative"
                      count={count}
                      showZero
                      overflowCount={999}
                    />
                  ) : null}
                </span>
              </Tab>
            );
          })}
        </TabList>
        <RefreshControl />
      </div>
    </div>
  );
}
